import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { CustomInput } from "global/components";
import { CustomContactNumberInput } from "global/components/CustomContactNumberInput/CustomContactNumberInput";
import notifiers from "global/constants/NotificationConstants";
import strings from "global/constants/StringConstants";
import title from "global/constants/TitleConstants";
import urls from "global/constants/UrlConstants";
import viewpaths from "global/constants/ViewPathConstants";
import { isTruthy, openErrorNotification } from "helpers/methods";
import AuthLayout from "layouts/AuthLayout/AuthLayout";
import { RegisterUser } from "models/interfaces";
import { useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useLocation } from "react-router-dom";
import history from "utils/history";
import { useTitle } from "utils/UseTitle";
import {
  registerFormField,
  registrationValidation,
} from "./AuthTypesAndValidation";
import hidePasswordIcon from "assets/images/Hide.svg";
import showPasswordIcon from "assets/images/Show.svg";
import { createNewAccount } from "./AuthService";
import authStyles from "./Auth.styles";
import { selectAuthenticated } from "redux/authSlice";
import { useAuthRouteRedirection } from "layouts/Config";
import { useAppSelector } from "utils/hooks";
import pandaIcon from "assets/icons1/panda_icon.svg";
import mobilePandaIcon from "assets/icons1/mobile_panda_icon.svg";
import Clarity from "@microsoft/clarity";

const Register = () => {
  const classes = authStyles;
  useTitle(title.REGISTER);
  const isLoggedIn = useAppSelector(selectAuthenticated);
  const authRoute = useAuthRouteRedirection();
  const [formFields, setFormFields] = useState(registerFormField);
  const [showPassword, setSetPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const planIdFromURL = new URLSearchParams(useLocation().search).get("id");
  const { executeRecaptcha } = useGoogleReCaptcha();
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));

  const handleOnchange = (event: React.ChangeEvent<any>) => {
    setFormFields({
      ...formFields,
      [event.target.name]: {
        ...formFields[event.target.name],
        value: event.target.value,
        error: "",
      },
    });
  };

  const createAccount = async (userData: RegisterUser) => {
    try {
      let token = "";
      if (urls.PROD) {
        token = await executeRecaptcha!();
      }
      setLoading(true);
      userData.captchaToken = token;
      await createNewAccount(userData);
      if(urls.PROD) {
        Clarity.setTag("account", userData.account);
        Clarity.setTag("email", userData.email);
        Clarity.identify(userData.email, userData.account, userData.account);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      if (error.message === strings.pendingVerificationErrorMessage) {
        history.push({
          pathname: viewpaths.verificationPendingViewPath,
          state: { email: userData.email, isFromRegistrationScreen: true },
        });
      } else {
        openErrorNotification(error.message);
      }
      throw error;
    }
  };

  const handleKeypress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmitButton();
    }
  };

  const handleSubmitButton = async () => {
    try {
      if (handleValidation()) {
        const userData: RegisterUser = {
          pwd: formFields.password?.value,
          authToken: "",
          name: formFields.firstName?.value + " " + formFields.lastName?.value,
          contactNo: formFields.contact?.value,
          email: formFields.email?.value,
          role: "",
          resources: [],
          account: formFields.company?.value,
          company: formFields.company?.value,
          captchaToken: "",
          planId: planIdFromURL ?? strings.FREE_PLAN_ID,
        };
        await createAccount(userData);
        history.push({
          pathname: viewpaths.registrationSuccessViewPath,
          state: { email: formFields.email?.value },
        });
      }
    } catch (error: any) {
      if (error.message !== strings.pendingVerificationErrorMessage) {
        openErrorNotification(
          isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
        );
      }
    }
  };

  const logInPage = () => {
    history.push(viewpaths.loginViewPath);
  };

  const handleValidation = () => {
    const { errors, isValid } = registrationValidation(formFields);
    setFormFields({ ...errors });
    return isValid;
  };

  const getForm = () => {
    return (
      <CardContent
        sx={{
          padding: "16px 24px",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} xl={6} lg={6} mt={1}>
            <CustomInput
              label="First Name"
              required
              placeHolder="Enter your first name"
              type="text"
              name="firstName"
              id="firstName"
              value={formFields.firstName.value}
              propsToInputElement={{ maxlength: 20 }}
              onChange={handleOnchange}
              onKeyPress={handleKeypress}
              error={
                !isTruthy(formFields.firstName.value) &&
                formFields.firstName.error
              }
              helperText={
                !isTruthy(formFields.firstName.value)
                  ? formFields.firstName.error
                  : ""
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} xl={6} lg={6} mt={1}>
            <CustomInput
              label="Last Name"
              required
              id="lastName"
              placeHolder="Enter your last name"
              type="text"
              name="lastName"
              value={formFields.lastName.value}
              propsToInputElement={{ maxlength: 20 }}
              onChange={handleOnchange}
              onKeyPress={handleKeypress}
              error={
                !isTruthy(formFields.lastName.value) &&
                formFields.lastName.error
              }
              helperText={
                !isTruthy(formFields.lastName.value)
                  ? formFields.lastName.error
                  : ""
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} xl={6} lg={6} mt={1}>
            <CustomInput
              label="Company Name"
              required
              id="companyName"
              placeHolder="Enter your company name"
              type="text"
              name="company"
              value={formFields.company.value}
              propsToInputElement={{ maxlength: 25 }}
              onChange={handleOnchange}
              onKeyPress={handleKeypress}
              error={
                !isTruthy(formFields.company.value) && formFields.company.error
              }
              helperText={
                !isTruthy(formFields.company.value)
                  ? formFields.company.error
                  : ""
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6} mt={1}>
            <CustomContactNumberInput
              label="Contact Number"
              required
              id="contactNumber"
              value={formFields.contact?.value}
              placeHolder="Enter contact number"
              onChange={(phone: any) => {
                setFormFields({
                  ...formFields,
                  contact: {
                    value: phone,
                    error: "",
                  },
                });
              }}
              error={
                isTruthy(formFields.contact.error) && formFields.contact.error
              }
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} xl={12} lg={12} mt={1}>
            <CustomInput
              label="Email"
              required
              id="email"
              placeHolder="Enter your email"
              type="email"
              name="email"
              value={formFields.email.value}
              propsToInputElement={{ maxlength: 45 }}
              onChange={handleOnchange}
              onKeyPress={handleKeypress}
              error={
                !isTruthy(formFields.email.value) && formFields.email.error
              }
              helperText={
                !isTruthy(formFields.email.value) ? formFields.email.error : ""
              }
            />
            {!strings.regex.test(formFields.email.value) &&
              formFields.email.value.length > 0 && (
                <FormHelperText error sx={{ ml: 2 }}>
                  Please enter valid email id
                </FormHelperText>
              )}
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12} mt={1}>
            <CustomInput
              label="Password"
              required
              placeHolder="••••••••"
              id="password"
              type={showPassword ? "text" : "password"}
              name="password"
              value={formFields.password.value}
              onChange={handleOnchange}
              onKeyPress={handleKeypress}
              error={
                !isTruthy(formFields.password.value.trim()) &&
                formFields.password.error
              }
              helperText={
                !isTruthy(formFields.password.value.trim())
                  ? formFields.password.error
                  : ""
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setSetPassword(!showPassword)}
                      edge="end"
                    >
                      <Box
                        component="img"
                        src={showPassword ? showPasswordIcon : hidePasswordIcon}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {formFields.password.value &&
              !strings.passwordValidationRegex.test(
                formFields.password.value.trim()
              ) && (
                <FormHelperText error sx={{ ml: 2 }}>
                  Please enter a password that is at least 8 characters long and
                  includes at least one digit, one lowercase letter, one
                  uppercase letter, and one special character from the
                  following: @ $ ! % * ? & #.
                </FormHelperText>
              )}
          </Grid>
          <Grid item xs={12} sm={12} md={12} xl={12} lg={12}>
            <Button
              fullWidth
              size="medium"
              sx={{ mt: 4 }}
              type="submit"
              variant="contained"
              onClick={handleSubmitButton}
              disabled={loading}
            >
              Create Account
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    );
  };

  const registerScreen = () => {
    return (
      <Box sx={{ position: "relative" }}>
        <Box
          component="img"
          src={mdUp ? pandaIcon : mobilePandaIcon}
          alt="Panda"
          sx={{
            position: "absolute",
            width: mdUp ? "150px" : "100px",
            height: "auto",
            right: mdUp ? "-86px" : "50%",
            left: mdUp ? "auto" : "50%",
            top: mdUp ? "50%" : "-70px",
            transform: mdUp ? "translateY(-50%)" : "translateX(-50%)",
            zIndex: 2,
          }}
        />
        <Card elevation={20} sx={classes.cardBox}>
          <CardHeader
            subheader={
              <Typography
                color="text.secondary"
                variant="body2"
                textAlign={"center"}
                gutterBottom
              >
                Already have an account?
                <span onClick={logInPage} style={classes.btnStyle}>
                  Login
                </span>{" "}
              </Typography>
            }
            sx={{ p: 0 }}
            title={
              <Typography
                color="text.primary"
                variant="h4"
                textAlign={"center"}
                gutterBottom
              >
                Create Account
              </Typography>
            }
          />

          {getForm()}
          <Typography variant="body2" fontSize="11px" mt={2} textAlign="center">
            By creating an account, you agree to our{" "}
            <a href="https://mailzzy.com/terms-of-use/" target="_blank">
              <span>Terms</span>
            </a>{" "}
            and have read and acknowledge the{" "}
            <a href="https://mailzzy.com/privacy-policy/" target="_blank">
              <span> Privacy Policy</span>
            </a>{" "}
            .
          </Typography>
        </Card>
      </Box>
    );
  };

  if (isLoggedIn) {
    history.push(authRoute);
    return null;
  } else {
    return <AuthLayout>{registerScreen()}</AuthLayout>;
  }
};
export default Register;
